import type { Database } from '@planmax/shared-types'
import type { BadgeProps } from '@/components/ui/badge'

// ============================================
// Enums from Database
// ============================================

export type ConflictStatus = Database['public']['Enums']['conflict_status']
export type ConflictSeverity = Database['public']['Enums']['conflict_severity']
export type ResolutionType = Database['public']['Enums']['resolution_type']

// ============================================
// Enum Arrays for Iteration
// ============================================

export const CONFLICT_STATUSES = ['open', 'resolved', 'false_alarm'] as const

export const CONFLICT_SEVERITIES = ['warning', 'critical'] as const

export const RESOLUTION_TYPES = [
  'transient_relocated',
  'transient_to_ramp',
  'transient_cancelled',
  'based_to_ramp_temp',
  'manual_restack',
  'false_alarm',
] as const

// ============================================
// Enum Labels
// ============================================

export const CONFLICT_STATUS_LABELS: Record<ConflictStatus, string> = {
  open: 'Open',
  resolved: 'Resolved',
  false_alarm: 'False Alarm',
}

export const CONFLICT_SEVERITY_LABELS: Record<ConflictSeverity, string> = {
  warning: 'Warning',
  critical: 'Critical',
}

export const RESOLUTION_TYPE_LABELS: Record<ResolutionType, string> = {
  transient_relocated: 'Transient Relocated',
  transient_to_ramp: 'Transient to Ramp',
  transient_cancelled: 'Transient Cancelled',
  based_to_ramp_temp: 'Based to Ramp (Temp)',
  manual_restack: 'Manual Restack',
  false_alarm: 'False Alarm',
}

// ============================================
// Badge Variants for Status & Severity
// ============================================

type BadgeVariant = BadgeProps['variant']

export const CONFLICT_STATUS_VARIANTS: Record<ConflictStatus, BadgeVariant> = {
  open: 'secondary',
  resolved: 'default',
  false_alarm: 'outline',
}

export const CONFLICT_SEVERITY_VARIANTS: Record<ConflictSeverity, BadgeVariant> = {
  warning: 'secondary',
  critical: 'destructive',
}

// ============================================
// Table Row Type (with joined data)
// ============================================

export interface SpaceConflictTableRow {
  // Core
  id: string
  organization_id: string
  location_id: string
  unit_id: string
  flight_schedule_id: string | null

  // Location & Unit (with joined names)
  location_name: string | null
  unit_code: string | null

  // Conflict details
  trigger_type: string
  conflict_date: string
  detected_at: string
  severity: ConflictSeverity
  shortfall_sqft: number | null
  affected_transient_ids: string[] | null

  // Resolution
  status: ConflictStatus
  resolution_type: ResolutionType | null
  resolution_notes: string | null
  resolved_at: string | null
  resolved_by: string | null

  // Audit
  created_at: string
}

// ============================================
// Update Type (for resolution)
// ============================================

export interface SpaceConflictUpdate {
  status: ConflictStatus
  resolution_type?: ResolutionType | null
  resolution_notes?: string | null
  resolved_at?: string
  resolved_by?: string
}

// ============================================
// Filter Type
// ============================================

export interface SpaceConflictFilters {
  search?: string
  status?: ConflictStatus[]
  severity?: ConflictSeverity[]
  locationId?: string
  dateFrom?: string
  dateTo?: string
}

// ============================================
// Helper Functions
// ============================================

/**
 * Format datetime for display
 */
export function formatDateTime(dateString: string | null): string {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Format date only for display
 */
export function formatDate(dateString: string | null): string {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Format number as sqft
 */
export function formatSqft(sqft: number | null): string {
  if (sqft === null) return '-'
  return `${sqft.toLocaleString()} sq ft`
}

/**
 * Get display name for space conflict
 */
export function getConflictDisplayName(
  conflict: Pick<SpaceConflictTableRow, 'unit_code' | 'conflict_date' | 'trigger_type'>
): string {
  const parts = [conflict.unit_code || 'Unknown Unit', formatDate(conflict.conflict_date)]
  return parts.join(' - ')
}
